import { useState, useEffect } from "react";
import useAuth from "../../Users/hooks/useAuth";

const ProcessFile = ({ book, file, model_size, transcriptHandler }) => {
    const { auth, setAuth } = useAuth();
    const [committed, setCommitted] = useState(false);
    const [submitted, setSubmitted] = useState([]);
    const [published, setPublished] = useState(false);
    const [change, setChange] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [pollingCount, setPollingCount] = useState(0);

    // Polling effect - checks for transcript completion
    useEffect(() => {
        let intervalId;

        if (isLoading || committed) {
            intervalId = setInterval(() => {
                console.log("Polling for transcript completion...");
                getFiles();
                setPollingCount(prev => prev + 1);
            }, 5000); // Check every 5 seconds
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isLoading, committed]);

    // Stop polling after a certain number of attempts (1 minute)
    useEffect(() => {
        if (pollingCount > 60) {
            setIsLoading(false);
            setCommitted(false);
            setError("Transcription is taking longer than expected. Please check back later.");
        }
    }, [pollingCount]);

    const onCommit = async () => {
        console.log("onCommit called with model:", model_size);
        setIsLoading(true);
        setCommitted(true);
        setError("");
        setPollingCount(0);

        try {
            const token = localStorage.getItem("token");
            const outdata = {
                "book": book, 
                "file": file, 
                "size_of_model": model_size
            };          
            
            console.log("Sending request with data:", outdata);

            const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/whisper/send`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(outdata),
            });
            
            if (response.ok) {
                console.log("Transcription started successfully");
                // Start immediate polling
                setTimeout(() => {
                    getFiles();
                }, 2000);
            } else {
                const errorText = await response.text();
                throw new Error(`Transcription failed: ${response.status} ${errorText}`);
            }
        } catch (err) {
            setError(err.message);
            console.error("Error:", err);
            setIsLoading(false);
            setCommitted(false);
        }
    };

    const getTranscript = async (size) => {
        const token = localStorage.getItem("token");
        const outdata = {
            "author": book.author, 
            "file": file, 
            'size_of_model': size, 
            'title': book.title, 
            'language': book.language, 
            "type": book.type
        };          
        
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/whisper/get-transcript`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(outdata),
        });
        
        if (response.ok) {
            const json = await response.json();
            transcriptHandler({
                'transcript': json, 
                'language': book.language, 
                'author': book.author, 
                'title': book.title, 
                'file': file, 
                'type': book.type
            });
        }
    };

    const resegmentateTranscript = async (size) => {
        const token = localStorage.getItem("token");
        const outdata = {
            "author": book.author, 
            "file": file, 
            'size_of_model': size, 
            'title': book.title, 
            'language': book.language
        };          
        
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/whisper/resegmentate-transcript`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(outdata),
        });
        
        if (response.ok) {
            const json = await response.json();
            setChange(json);
        }
    };
    
    const publishTranscript = async (size) => {
        const token = localStorage.getItem("token");
        const outdata = {
            "author": book.author, 
            "file": file, 
            'size_of_model': size, 
            'title': book.title, 
            'language': book.language
        };          
        
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/whisper/publish-transcript`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(outdata),
        });
        
        if (response.ok) {
            const json = await response.json();
            setPublished({'model_size': size, 'published': true});
        }
    };

    const deleteTranscript = async (size) => {
        const token = localStorage.getItem("token");
        const outdata = {
            "author": book.author, 
            "file": file, 
            'size_of_model': size, 
            'title': book.title, 
            'language': book.language, 
            'type': book.type
        };          
        
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/whisper/delete-transcript`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(outdata),
        });
        
        if (response.ok) {
            const json = await response.json();
            transcriptHandler();
            setChange(json);
        }
    };

    const retractTranscript = async (size) => {
        const token = localStorage.getItem("token");
        const outdata = {
            "author": book.author, 
            "file": file, 
            'size_of_model': size, 
            'title': book.title, 
            'language': book.language
        };          
        
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/whisper/retract-transcript`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(outdata),
        });
        
        if (response.ok) {
            const json = await response.json();
            setPublished({'model_size': size, 'published': false});
        }
    };

    const onRefresh = async () => {
        const token = localStorage.getItem("token");
        const outdata = {
            "author": book.author, 
            "title": book.title, 
            "language": book.language
        };
             
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/whisper/get-submitted-files`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(outdata),
        });
        
        if (response.ok) {
            const json = await response.json();
            return json;
        } else {
            return [];
        }
    };

    const getFiles = async () => {
        const json = await onRefresh();
        if (json) {
            setSubmitted(json);
            
            // Check if our current file has completed transcription
            const currentFileData = json.find(item => item[0] === file);
            if (currentFileData && (isLoading || committed)) {
                const hasCompleted = currentFileData[1].some(size => 
                    size.model_size === model_size
                );
                
                if (hasCompleted) {
                    console.log("Transcription completed for model:", model_size);
                    setIsLoading(false);
                    setCommitted(false);
                    setPollingCount(0);
                }
            }
        }
    };

    useEffect(() => {
        getFiles();
    }, [committed, published, change]);

    return (
        <div className="flex flex-col items-center p-5 gap-5 border-2 rounded-xl bg-gray-100 shadow-sm">
            <p className="text-md font-semibold text-gray-800 truncate max-w-full">{file}</p>
            <p className="text-sm text-gray-600">Selected model: <span className="font-medium">{model_size}</span></p>
            
            <button 
                onClick={onCommit}
                disabled={isLoading}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    isLoading 
                    ? "bg-gray-400 text-gray-600 cursor-not-allowed" 
                    : "bg-blue-500 hover:bg-blue-600 text-white shadow-sm hover:shadow-md"
                }`}
                type="button"
            >
                {isLoading ? (
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing... ({pollingCount})
                    </div>
                ) : (
                    "Transcribe"
                )}
            </button>

            {error && (
                <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error}
                </div>
            )}

            {submitted.length > 0 && (
                <div className="w-full">
                    <p className="text-lg font-semibold text-blue-500 text-center mb-3">Available Transcripts</p>
                    <div className="space-y-3">
                        {submitted.map((item, i) => (
                            item[0] === file && (
                                <div key={i} className="space-y-2">
                                    {item[1].map((size, j) => (
                                        <div key={j} className="grid grid-cols-1 sm:grid-cols-5 gap-2 p-3 bg-white rounded-lg border">
                                            <button 
                                                className="px-3 py-2 rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-medium transition-colors"
                                                onClick={() => getTranscript(size.model_size)}
                                            >
                                                {size.model_size}
                                            </button>
                                            
                                            <button 
                                                onClick={() => publishTranscript(size.model_size)}
                                                className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                                                    size.hasOwnProperty('published') && size.published 
                                                    ? "bg-green-200 hover:bg-green-300 text-green-800" 
                                                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                                                }`}
                                            >
                                                Publish
                                            </button>

                                            <button 
                                                className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                                                    size.hasOwnProperty('published') && size.published 
                                                    ? "bg-red-200 hover:bg-red-300 text-red-800" 
                                                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                                                }`}
                                                onClick={() => retractTranscript(size.model_size)} 
                                            >
                                                Retract
                                            </button>
                                            
                                            <button 
                                                className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                                                    size.hasOwnProperty('published') && size.published 
                                                    ? "bg-red-200 hover:bg-red-300 text-red-800" 
                                                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                                                }`}
                                                onClick={() => resegmentateTranscript(size.model_size)} 
                                            >
                                                Resegment
                                            </button>

                                            <button 
                                                onClick={() => deleteTranscript(size.model_size)}
                                                className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                                                    size.hasOwnProperty('published') && size.published 
                                                    ? "bg-green-200 hover:bg-green-300 text-green-800" 
                                                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                                                }`}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProcessFile;